/**
 * Severities that count as "real" cluster-audit findings.
 *
 * Mirrors apps/api/src/utils/auditSeverity.ts — keep the two in lockstep.
 * The backend uses this list to drive list-view statuses, verification-tab
 * aggregates, and the new-audit status collapse. The frontend uses it to
 * compute the effective status displayed in the audit-detail drawer so the
 * badge matches what the cluster list shows.
 *
 * LOW / INFO are still rendered in the audit drawer (the user can see
 * them) — they just don't flag the cluster or roll up into any aggregate.
 */
export const SIGNIFICANT_SEVERITIES: DiscrepancySeverity[] = [
  "HIGH",
  "MEDIUM",
];

export const isSignificantSeverity = (
  severity?: DiscrepancySeverity | string | null
): boolean =>
  severity != null &&
  SIGNIFICANT_SEVERITIES.includes(severity as DiscrepancySeverity);

/**
 * Effective status for an audit. A DISCREPANCIES_FOUND audit whose every
 * discrepancy is LOW/INFO collapses to APPROVED so the badge in the audit
 * drawer matches what the list view shows. Caller still renders the full
 * stored discrepancy list below the badge.
 */
export const getEffectiveAuditStatus = (
  audit:
    | { status?: ClusterAuditStatus; discrepancies?: { severity?: DiscrepancySeverity }[] }
    | null
    | undefined
): ClusterAuditStatus | undefined => {
  if (!audit?.status) return audit?.status;
  if (audit.status !== "DISCREPANCIES_FOUND") return audit.status;
  const hasSignificant = (audit.discrepancies ?? []).some((d) =>
    isSignificantSeverity(d?.severity)
  );
  return hasSignificant ? "DISCREPANCIES_FOUND" : "APPROVED";
};
